import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Column, Row } from "@components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import Input from "@components/Input";
import Loading from "@components/Loading";
import Tooltip from "@components/Tooltip";
import Switch from "@components/Switch";
import { ReactComponent as InfoIcon } from "@assets/icons/info.svg";
import { useLang } from "@src/hooks/useLang";
import {
  getCropwiseStatus,
  connectCropwise,
  disconnectCropwise,
  getCropwiseSyncSettings,
  putCropwiseSyncSettings,
} from "@services/cropwiseService";
import { useIntegrationSyncSettings, SyncUnitsFields } from "@screens/ProfileScreen/IntegrationSyncSettings";
import IntegrationCard, { CollapsedSetup } from "@screens/ProfileScreen/IntegrationCard";
import { observer } from "mobx-react-lite";

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AUTO_SYNC_HINT =
  "Your harvested fields sync to Cropwise Operations automatically — once a day when the yield or calibration changes. Turn this off to sync only manually.";

const SYNC_UNITS_HINT =
  "Units used for the yield maps sent to Cropwise. This is set for the whole account — team members and automatic syncs all use it.";

/** Only ever link to Cropwise's own domain, even though the URL comes from our backend. */
const sanitizeCropwiseUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== "https:") return null;
    if (host !== "cropwise.com" && !host.endsWith(".cropwise.com")) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const DEFAULT_TOKEN_PAGE = "https://operations.cropwise.com/user/linked_devices";

const CropwiseProfileSection: React.FC = () => {
  const lang = useLang();
  const cwLang = (lang as any).cropwise ?? {};

  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [tokenPageUrl, setTokenPageUrl] = useState<string>(DEFAULT_TOKEN_PAGE);
  const [token, setToken] = useState("");
  // Setup form is collapsed until the user asks for it (disconnected cards
  // otherwise fill the grid with instructions nobody using the OTHER
  // integration needs to see).
  const [setupOpen, setSetupOpen] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sync = useIntegrationSyncSettings(connected, getCropwiseSyncSettings, putCropwiseSyncSettings);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const status = await getCropwiseStatus();
      setConnected(status.connected);
      setAccountId(status.accountId);
      const safe = sanitizeCropwiseUrl(status.tokenPageUrl);
      if (safe) setTokenPageUrl(safe);
    } catch {
      // A 404 here means the feature config isn't enabled for this user — the
      // section shouldn't be rendered at all in that case, so stay quiet.
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleConnect = async () => {
    const trimmed = token.trim();
    if (!trimmed || connectLoading) return;
    setConnectLoading(true);
    setError(null);
    try {
      const r = await connectCropwise(trimmed);
      setConnected(r.connected);
      setAccountId(r.accountId);
      // Drop the token from component state the moment it's stored — no reason
      // to keep a live third-party credential sitting in the React tree.
      setToken("");
    } catch (e: any) {
      setError(e?.message ?? "Could not connect to Cropwise");
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      await disconnectCropwise();
      setConnected(false);
      setAccountId(null);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const title = cwLang.integrationTitle ?? "Cropwise Operations";

  if (loading) {
    return (
      <IntegrationCard title={title}>
        <Loading />
      </IntegrationCard>
    );
  }

  // Disconnected and not being set up: one line and a button. The three-step
  // token walkthrough is only useful to someone actually connecting, and a
  // John Deere user shouldn't scroll past it every time they open Settings.
  if (!connected && !setupOpen) {
    return (
      <IntegrationCard
        title={title}
        connected={false}
        disconnectedLabel={cwLang.statusNotConnected ?? "Not connected"}
      >
        {error && (
          <>
            <Text size={14} color="#dc2626">
              {error}
            </Text>
            <SizedBox height={8} />
          </>
        )}
        <CollapsedSetup
          description={
            cwLang.collapsedDescription ??
            "Import field boundaries from Cropwise and send yield maps back."
          }
          buttonLabel={cwLang.connectButton ?? "Connect Cropwise"}
          onExpand={() => setSetupOpen(true)}
        />
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      title={title}
      connected={connected}
      connectedLabel={cwLang.statusConnected ?? "Connected"}
      disconnectedLabel={cwLang.statusNotConnected ?? "Not connected"}
    >
      {error && (
        <>
          <Text size={14} color="#dc2626">
            {error}
          </Text>
          <SizedBox height={8} />
        </>
      )}
      {connected ? (
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          {accountId && (
            <Text size={12} color="#899093">
              {accountId}
            </Text>
          )}
          <ToggleRow>
            <Text size={14} color="#374151">
              {cwLang.autoSyncLabel ?? "Automatic sync"}
            </Text>
            <Tooltip
              content={
                <Text size={12} style={{ maxWidth: 260, display: "block" }}>
                  {cwLang.autoSyncHint ?? AUTO_SYNC_HINT}
                </Text>
              }
            >
              <InfoIcon />
            </Tooltip>
            <div style={{ flex: 1 }} />
            <Switch
              on={sync.settings.autoSyncYields}
              disabled={!sync.loaded || sync.saving}
              onToggle={() => sync.save({ autoSyncYields: !sync.settings.autoSyncYields })}
              ariaLabel="Toggle automatic Cropwise sync"
            />
          </ToggleRow>
          <div>
            <ToggleRow>
              <Text size={14} color="#374151">
                {cwLang.syncUnitsLabel ?? "Units sent to Cropwise"}
              </Text>
              <Tooltip
                content={
                  <Text size={12} style={{ maxWidth: 260, display: "block" }}>
                    {cwLang.syncUnitsHint ?? SYNC_UNITS_HINT}
                  </Text>
                }
              >
                <InfoIcon />
              </Tooltip>
            </ToggleRow>
            <SizedBox height={8} />
            <SyncUnitsFields
              settings={sync.settings}
              disabled={!sync.loaded || sync.saving}
              onChange={sync.save}
            />
          </div>
          <div
            style={{
              background: "#FFFBEB",
              border: "1px solid #FCD34D",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text size={12} color="#92400E" style={{ display: "block" }}>
              {cwLang.yieldBetaNote ??
                "Yield sync to Cropwise is still being confirmed with Syngenta. Field import works; if a yield upload is rejected, the exact response is recorded so we can follow it up."}
            </Text>
          </div>
          <Button
            kind="outline"
            onClick={handleDisconnect}
            disabled={disconnectLoading}
            style={{ maxWidth: 160 }}
          >
            {disconnectLoading ? <Loading /> : (cwLang.disconnectButton ?? "Disconnect")}
          </Button>
        </Column>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <div
            style={{
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text size={13} color="#166534" style={{ display: "block", marginBottom: 6 }}>
              {cwLang.connectHint ?? "Cropwise connects with an API token, not a login."}
            </Text>
            <ol style={{ margin: 0, paddingLeft: 18, color: "#15803d", fontSize: 12 }}>
              <li>
                {cwLang.connectStep1 ?? "Open"}{" "}
                <a
                  href={tokenPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "#15803d" }}
                >
                  {cwLang.connectStep1Link ?? "Cropwise → Profile → Linked devices"}
                </a>
              </li>
              <li>{cwLang.connectStep2 ?? "Create an API token and copy it"}</li>
              <li>{cwLang.connectStep3 ?? "Paste it below and press Connect"}</li>
            </ol>
          </div>
          <div style={{ maxWidth: 420 }}>
            <Input
              label={cwLang.tokenLabel ?? "Cropwise API token"}
              // type="password" so the token isn't left on screen; the component
              // provides its own show/hide toggle.
              type="password"
              value={token}
              placeholder={cwLang.tokenPlaceholder ?? "Paste your Cropwise API token"}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConnect();
              }}
              autoComplete="off"
            />
          </div>
          <Row style={{ gap: 8 }}>
            <Button
              onClick={handleConnect}
              disabled={!token.trim() || connectLoading}
              style={{ maxWidth: 200 }}
            >
              {connectLoading ? <Loading /> : (cwLang.connectButton ?? "Connect Cropwise")}
            </Button>
            <Button
              kind="outline"
              onClick={() => {
                setSetupOpen(false);
                setToken(""); // don't leave a half-typed credential in state
                setError(null);
              }}
              style={{ maxWidth: 120 }}
            >
              {cwLang.cancel ?? "Cancel"}
            </Button>
          </Row>
          <Row>
            <Text size={12} color="#899093">
              {cwLang.tokenPrivacy ??
                "The token is stored encrypted and used only to read your fields and send yield maps back."}
            </Text>
          </Row>
        </Column>
      )}
    </IntegrationCard>
  );
};

export default observer(CropwiseProfileSection);
